import React, { useState, useEffect } from 'react';
import Modal from '../../../components/ui/Modal';
import { eventsApi } from '../../../api/eventsApi';
import {
  ChatBubbleLeftRightIcon,
  ShareIcon,
  QuestionMarkCircleIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline';
import {
  FormActions,
  FormError,
  FormField,
  FormIntro,
  FormOption,
  FormOptionList,
  GenerateLabel,
} from '../../../components/ui/FormModalParts';
import { splitTrimmed } from '../../../utils/text';

const DEFAULT_OPTIONS = { chatbot: true, graph: true, qa: false, push_to_wordpress: false };

const REPORT_OPTIONS = [
  {
    key: 'chatbot',
    Icon: ChatBubbleLeftRightIcon,
    title: 'Include chatbot',
    description: 'Embed the MyDepartment chatbot linked to this event. Set it below or in Edit event.',
  },
  {
    key: 'graph',
    Icon: ShareIcon,
    title: 'Generate knowledge graph',
    description: 'Create an interactive graph showing speakers, arguments, and topics.',
  },
  {
    key: 'qa',
    Icon: QuestionMarkCircleIcon,
    title: 'Q&A analysis',
    description: 'Analyze specific questions across all session transcripts.',
  },
  {
    key: 'push_to_wordpress',
    Icon: GlobeAltIcon,
    title: 'Publish to WordPress',
    description: 'Automatically publish the event report to WordPress after generation.',
  },
];

/** A saved flag from the last report wins over the current value. */
const savedFlag = (saved, current) => (saved != null ? !!saved : current);

const EventReportModal = ({ isOpen, onClose, event, onReportGenerated }) => {
  const [options, setOptions] = useState(DEFAULT_OPTIONS);
  const [questions, setQuestions] = useState('');
  const [faiconPostId, setFaiconPostId] = useState('');
  const [chatbotId, setChatbotId] = useState('');
  const [coverImageUrl, setCoverImageUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Reset state when modal opens, then prefill from the last report so the
  // user doesn't have to retype questions / chatbot id every time.
  useEffect(() => {
    if (!isOpen) return;

    setError('');
    setOptions(DEFAULT_OPTIONS);
    setQuestions('');
    const linkedFaiconId = event?.faicon_id || event?.faiconId || '';
    const linkedWpId = event?.wp_id || event?.wpId || '';
    setFaiconPostId(String(linkedFaiconId || linkedWpId || ''));
    setChatbotId(String(event?.chatbot_id || event?.chatbotId || ''));
    setCoverImageUrl(String(event?.image_url || event?.imageUrl || ''));

    if (!event?.id) return;

    let cancelled = false;
    (async () => {
      try {
        const status = await eventsApi.getReportStatus(event.id);
        if (cancelled || !status || status.status === 'NO_REPORT') return;

        if (!event?.chatbot_id && !event?.chatbotId && status.chatbot_id) {
          setChatbotId(String(status.chatbot_id));
        }
        const savedQuestions = Array.isArray(status.questions) ? status.questions : [];
        if (savedQuestions.length > 0) {
          setQuestions(savedQuestions.join('\n'));
        }
        setOptions((prev) => ({
          ...prev,
          chatbot: savedFlag(status.chatbot_enabled, prev.chatbot),
          graph: savedFlag(status.graph_enabled, prev.graph),
          qa: savedFlag(status.qa_enabled, savedQuestions.length > 0),
        }));
      } catch (err) {
        // Prefill is best-effort; ignore errors and keep defaults.
      }
    })();

    return () => { cancelled = true; };
  }, [isOpen, event]);

  const toggleOption = (option) => {
    setOptions((prev) => ({ ...prev, [option]: !prev[option] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const questionsList = splitTrimmed(questions);
      const payload = {
        questions: questionsList,
        chatbot: options.chatbot,
        qa: options.qa && questionsList.length > 0,
        graph: options.graph,
        push_to_wordpress: options.push_to_wordpress,
      };
      if (faiconPostId.trim()) payload.faicon_post_id = faiconPostId.trim();
      if (chatbotId.trim()) payload.chatbot_id = chatbotId.trim();
      if (coverImageUrl.trim()) payload.cover_image_url = coverImageUrl.trim();
      await eventsApi.generateReport(event.id, payload);

      if (onReportGenerated) onReportGenerated('started');
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to start report generation');
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Generate Event Report" size="lg" className="event-modal">
      <form onSubmit={handleSubmit} className="event-modal-form">
        <FormIntro title={event?.title || 'Event report'}>
          Choose which assets should be generated from this event's transcripts.
        </FormIntro>

        <FormOptionList title="Report options">
          {REPORT_OPTIONS.map(({ key, Icon, title, description }) => (
            <FormOption
              key={key}
              checked={options[key]}
              onToggle={() => toggleOption(key)}
              disabled={loading}
              Icon={Icon}
              title={title}
            >
              {description}
            </FormOption>
          ))}
        </FormOptionList>

        <FormField
          id="faicon-post-id"
          label="Faicon Event ID (optional)"
          hint={<>If you plan to send this report to Faicon, enter the event ID here. It will be used as <code>wpPostId</code> in the report JSON.</>}
        >
          <input
            id="faicon-post-id"
            type="text"
            value={faiconPostId}
            onChange={(e) => setFaiconPostId(e.target.value)}
            className="event-modal-control"
            placeholder=""
            disabled={loading}
          />
        </FormField>

        <FormField
          id="chatbot-id"
          label="MyDepartment chatbot"
          hint="Taken from the event. Publishing a session report replaces it with the bot MyDepartment returns, and that bot is embedded on DigWatch and FAICON."
        >
          <input
            id="chatbot-id"
            type="text"
            value={chatbotId}
            onChange={(e) => setChatbotId(e.target.value)}
            className="event-modal-control"
            placeholder="e.g. 9a665f91-baef-4e60-8b0d-174f73d9515e"
            disabled={loading}
          />
        </FormField>

        <FormField
          id="cover-image-url"
          label="Cover image URL (optional)"
          hint="Cover image used on DigWatch when the report is published. Prefilled from the event image; saved back to the event."
        >
          <input
            id="cover-image-url"
            type="url"
            value={coverImageUrl}
            onChange={(e) => setCoverImageUrl(e.target.value)}
            className="event-modal-control"
            placeholder="https://example.com/cover.jpg"
            disabled={loading}
          />
        </FormField>

        {options.qa && (
          <FormField
            id="questions"
            label="Questions for analysis"
            hint="Enter one question per line. The AI will search all session transcripts to find relevant discussions."
          >
            <textarea
              id="questions"
              value={questions}
              onChange={(e) => setQuestions(e.target.value)}
              rows={5}
              className="event-modal-control"
              placeholder={'Enter one question per line, e.g.:\nWhat were the main conclusions?\nWhat topics received the most attention?\nWere there any disagreements?'}
              disabled={loading}
            />
          </FormField>
        )}

        <FormError message={error} />

        <FormActions onCancel={handleClose} disabled={loading}>
          <GenerateLabel starting={loading} label="Generate report" />
        </FormActions>
      </form>
    </Modal>
  );
};

export default EventReportModal;
